"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import { CheckList } from "@/components/tools/research-writing/check-list";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  buildProblemDocument,
  CONNECTORS,
  type ProblemInput,
  problemChecks,
  type StatementFormat,
  statementWords,
} from "@/lib/tool-kit/research-writing/problem-statement";

/** English strings. A translated content file overrides these through `ui`. */
export const PROBLEM_STATEMENT_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  topicHeading: "Your project",
  titleLabel: "Working title (optional)",
  titlePlaceholder: "e.g. Peer Tutoring and Retention",
  formatLabel: "Write it as",
  formats: { paragraph: "One paragraph", parts: "Four labelled parts" },
  partsHeading: "The four parts",
  idealLabel: "The ideal — what should be happening",
  idealPlaceholder:
    "Every first-year student who struggles academically should get help in time to stay enrolled.",
  idealHint: "A full sentence about the desired state, not about your study.",
  connectorLabel: "Connector between ideal and reality",
  connectors: {
    however: "However,",
    but: "But",
    yet: "Yet",
    reality: "In reality,",
    none: "None — I wrote the contrast myself",
  },
  realityLabel: "The reality — what is happening, and to whom",
  realityPlaceholder:
    "Nearly a third of first-year STEM students do not return for a second year.",
  realityHint: "A full sentence. The connector is added in front of it.",
  evidenceLabel: "Evidence for the reality (optional)",
  evidencePlaceholder:
    "Registrar data for the 2022–2024 cohorts show … (source).",
  consequencesLabel: "The consequences — what follows if nothing changes",
  consequencesPlaceholder:
    "Each student who leaves loses … and the university loses …",
  consequencesHint:
    "Whom does it cost, and how? A number is better than an adjective.",
  proposalLabel: "The proposal — what your study will do",
  proposalPlaceholder:
    "examine whether peer tutoring predicts second-year re-enrollment",
  proposalHint: "Start with a verb. Finishes “This study proposes to …”.",
  resultsHeading: "Checks",
  statWords: "Words in the statement",
  checksLabel: "Checklist",
  checkStates: { done: "Done", todo: "To do", note: "Check" },
  checkText: {
    ideal_done: "The ideal is stated.",
    ideal_todo: "State the ideal: what should be happening.",
    reality_done: "The reality is stated.",
    reality_todo: "State the reality: what is happening, and to whom.",
    evidence_done: "The reality is backed by a number, a citation or a source.",
    evidence_note:
      "Nothing backs the reality yet. Add evidence: a number, a citation or a named source.",
    consequences_done: "The consequences are stated.",
    consequences_todo: "Say what follows if nothing changes.",
    proposal_done: "The proposal is stated.",
    proposal_todo: "Say what your study will do.",
    solution_done:
      "The ideal, reality and consequences describe the problem, not a fix.",
    solution_note:
      "A solution seems to appear before the proposal. A problem statement describes the problem; keep what you will do for the last sentence.",
  },
  fileName: "problem-statement",
  document: {
    heading: "Your problem statement",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Problem Statement",
  subtitle: "Ideal, reality, consequences, proposal",
  heading: "Statement of the problem",
  headings: {
    ideal: "Ideal",
    reality: "Reality",
    consequences: "Consequences",
    proposal: "Proposal",
  },
  tip: "Put the ideal beside the reality so the gap shows, say what it costs, and only then say what you will do. The reader’s question is “So what?”",
  proposalFrame: "This study proposes to {proposal}.",
  prompts: {
    ideal: "State the ideal: “Ideally, [what should be happening].”",
    reality:
      "State the reality, with evidence: “[What is happening, to whom], according to [source].”",
    consequences:
      "State the consequences: “If this continues, [who is harmed, and how].”",
    proposal: "State the proposal: “This study proposes to [verb] [what].”",
  },
  example: {
    title: "Peer Tutoring and Retention",
    ideal:
      "Every first-year STEM student who struggles academically should get help in time to stay enrolled.",
    connector: "however",
    reality:
      "Nearly a third of first-year STEM students at the university do not return for a second year, and most of those who leave do so after failing a gateway course.",
    evidence:
      "Registrar data for the [years] cohorts show [N]% non-return ([source]).",
    consequences:
      "Each student who leaves loses the tuition already paid and the chance of a degree, and if nothing changes the tutoring budget will keep being spent without evidence of whom it helps.",
    proposal:
      "examine whether attending peer tutoring in the first semester predicts re-enrollment, and for whom",
  },
} as const;

const EMPTY: ProblemInput = {
  title: "",
  ideal: "",
  connector: "however",
  reality: "",
  evidence: "",
  consequences: "",
  proposal: "",
  format: "paragraph",
};

const FORMATS: readonly StatementFormat[] = ["paragraph", "parts"];

type TextKey = Exclude<keyof ProblemInput, "connector" | "format">;

export default function ProblemStatementGeneratorWidget() {
  const strings = useToolUi(PROBLEM_STATEMENT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<ProblemInput>(EMPTY);
  const labels = useMemo(
    () => ({
      ...strings,
      connectors: {
        however: strings.connectors.however,
        but: strings.connectors.but,
        yet: strings.connectors.yet,
        reality: strings.connectors.reality,
      },
    }),
    [strings]
  );
  const doc = useMemo(
    () => buildProblemDocument(input, labels, locale),
    [input, labels, locale]
  );
  const documentLabels = useMemo(() => strings.document, [strings]);
  const checkText = strings.checkText as Record<string, string>;
  const checks = problemChecks(input).map((check) => ({
    key: check.id,
    state: check.state,
    text: checkText[`${check.id}_${check.state}`] ?? "",
  }));
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const area = (
    key: TextKey,
    id: string,
    label: string,
    placeholder: string,
    hint?: string
  ) => (
    <TextAreaField
      hint={hint}
      id={`ps-${id}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      prose
      rows={3}
      value={input[key]}
    />
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput({ ...EMPTY, ...strings.example })}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.topicHeading}>
        <TextField
          id="ps-title"
          label={strings.titleLabel}
          onChange={update("title")}
          placeholder={strings.titlePlaceholder}
          value={input.title}
        />
        <PillGroup
          label={strings.formatLabel}
          name="ps-format"
          onChange={(format) => setInput((p) => ({ ...p, format }))}
          options={FORMATS.map((value) => ({
            value,
            label: strings.formats[value],
          }))}
          value={input.format}
        />
      </Panel>

      <Panel title={strings.partsHeading}>
        {area(
          "ideal",
          "ideal",
          strings.idealLabel,
          strings.idealPlaceholder,
          strings.idealHint
        )}
        <SelectField
          id="ps-connector"
          label={strings.connectorLabel}
          onChange={(connector) => setInput((p) => ({ ...p, connector }))}
          options={CONNECTORS.map((value) => ({
            value,
            label: strings.connectors[value],
          }))}
          value={input.connector}
        />
        {area(
          "reality",
          "reality",
          strings.realityLabel,
          strings.realityPlaceholder,
          strings.realityHint
        )}
        {area(
          "evidence",
          "evidence",
          strings.evidenceLabel,
          strings.evidencePlaceholder
        )}
        {area(
          "consequences",
          "consequences",
          strings.consequencesLabel,
          strings.consequencesPlaceholder,
          strings.consequencesHint
        )}
        {area(
          "proposal",
          "proposal",
          strings.proposalLabel,
          strings.proposalPlaceholder,
          strings.proposalHint
        )}
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.statWords}
            value={fmt(statementWords(input, labels, locale))}
          />
        </StatGrid>
        <CheckList
          items={checks}
          label={strings.checksLabel}
          stateLabels={strings.checkStates}
        />
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="ps"
        labels={documentLabels}
      />
    </div>
  );
}
